<template>
	<ui-dialog
		ref="dialog"
		title="Settings"
		closable
	>
		<div class="video-layout">
			<div>
				<ui-switch
					v-model="enableBfcp"
					:disabled="isProcessing || !isReady"
					:label="`Cloud mixing ${enableBfcp ? 'activated' : 'deactivated'}`"
					:tooltip="bfcpTooltip"
					large
				/>
				<ui-select
					v-show="enableBfcp"
					v-model="selectedVideoLayout"
					:disabled="isProcessing || !isReady"
					class="select"
					label="Layout (used when Content is shared)"
					@select="renderThumbnail(videoLayoutMap[selectedVideoLayout])"
				>
					<ui-select-item
						v-for="videoLayout in videoLayoutList"
						:key="videoLayout.videolayout_id"
						:disabled="videoLayout.wfactor !== 1 || videoLayout.hfactor !== 1"
						:label="videoLayout.name"
						:value="videoLayout.videolayout_id"
					/>
				</ui-select>
			</div>
			<div
				ref="thumbnail"
				:hidden="!enableBfcp"
				class="thumbnail"
			>
				<div ref="content" :hidden="!isReady" class="content"></div>
				<div ref="share" :hidden="!isReady" class="share"></div>
				<md-tooltip>{{ bfcpTooltip }}</md-tooltip>
			</div>
		</div>

		<div class="info">Note: When updating Settings, it may take up to 60 seconds before the change is activated. Any change will only apply to new sessions and it will not apply to ongoing sessions or existing recordings.</div>

		<ui-dialog-footer>
			<ui-button
				:disabled="isProcessing || !isReady"
				primary
				text="Apply"
				tooltip="Update video layout settings"
				@click="setVideoLayout"
			/>
		</ui-dialog-footer>
	</ui-dialog>
</template>

<script>
import alert from "lib/modules/Alert";
import http from "lib/modules/Http";
import UiButton from "lib/components/ui/Button";
import UiDialog from "lib/components/ui/Dialog";
import UiDialogFooter from "lib/components/ui/DialogFooter";
import UiSelect from "lib/components/ui/Select";
import UiSelectItem from "lib/components/ui/SelectItem";
import UiSwitch from "lib/components/ui/SwitchButton";

export default {
	name: "FeedbackDialog",
	components: {
		UiButton,
		UiDialog,
		UiDialogFooter,
		UiSelect,
		UiSelectItem,
		UiSwitch
	},
	props: {
		recorder: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			enableBfcp: this.recorder.bfcp,
			bfcpTooltip: "Cloud mixing can be used when Source is Video conference, and the remote side supports BFCP (separation of video and content). When Cloud mixing is activated, the video (blue) and content (gray) channels will be mixed as shown in the selected Layout when content is present. When Cloud mixing is deactivated, the mixing will be performed on the remote side.", // eslint-disable-line max-len
			selectedVideoLayout: this.recorder.videolayout_id,
			videoLayoutList: [],
			isProcessing: false,
			isReady: false
		};
	},
	mounted() {
		this.open = () => {
			this.isReady = false;

			Promise.all([
				this.getRecorder(),
				this.getVideoLayoutList()
			]).then(() => {
				this.renderThumbnail(this.videoLayoutMap[this.selectedVideoLayout]);
				this.isReady = true;
			})
			.catch((error) => {
				alert.error("Unable to get video layout list / recorder", error.response);
			});

			this.$refs.dialog.open();
		};
	},
	methods: {
		getRecorder() {
			return http.get(`/api/v2/recorder/${this.recorder.recorder_id}`)
			.then((response) => {
				this.enableBfcp = response.data.bfcp;
				this.selectedVideoLayout = response.data.videolayout_id;
			});
		},
		getVideoLayoutList() {
			return http.get("/api/v2/videolayoutlist")
			.then((response) => {
				this.videoLayoutList = response.data;
				this.videoLayoutMap = response.data.reduce((a, x) => ({ ...a, [x.videolayout_id]: x }), {});
			});
		},
		renderThumbnail(layout) {
			const thumbnail = this.$refs.thumbnail;
			const content = this.$refs.content;
			const share = this.$refs.share;

			thumbnail.removeAttribute("style");

			if (layout.wfactor > 1)
				thumbnail.style.height = `${parseInt(getComputedStyle(thumbnail).getPropertyValue("height")) / layout.wfactor}px`;

			if (layout.hfactor > 1)
				thumbnail.style.width = `${parseInt(getComputedStyle(thumbnail).getPropertyValue("width")) / layout.hfactor}px`;

			if (layout.content)
				this.setStyle(layout.content, content);
			else
				content.removeAttribute("style");

			if (layout.share)
				this.setStyle(layout.share, share);
			else
				share.removeAttribute("style");
		},
		setStyle(layoutStr, element) {
			const match = layoutStr.match(/(\d+),(\d+)\/(\d+)x(\d+)/);

			if (!match)
				return;

			element.style.top = `${match[2]}%`;
			element.style.left = `${match[1]}%`;
			element.style.width = `${match[3]}%`;
			element.style.height = `${match[4]}%`;
		},
		setVideoLayout() {
			this.isProcessing = true;

			http.patch(`/api/v2/recorder/${this.recorder.recorder_id}`, {
				bfcp: this.enableBfcp,
				videolayout_id: this.selectedVideoLayout
			})
			.then(() => {
				this.$refs.dialog.close();
			})
			.finally(() => {
				this.isProcessing = false;
			})
			.catch((error) => {
				alert.error("Unable to set video layout", error.response);
			});
		}
	}
};
</script>

<style lang="less" scoped>
.video-layout {
	display: grid;
	grid-template-columns: 1fr auto;
	grid-gap: 15px;
	@media (max-width: 682px) {
		grid-template-columns: none;
		grid-template-rows: auto;
		.thumbnail {
			justify-self: center;
		}
	}
	.select {
		margin-bottom: 0;
	}
	.thumbnail {
		position: relative;
		align-self: center;
		width: 256px;
		height: 144px;
		background-color: #000;
		.content {
			position: absolute;
			width: 100%;
			height: 100%;
			background-color: #5f646c;
		}
		.share {
			position: absolute;
			top: 3%;
			left: 73%;
			width: 25%;
			height: 25%;
			background-color: #23b8e5;
		}
	}
}
.info {
	font-style: italic;
	margin: 24px 0;
}
</style>
